import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ChatPanel from './ChatPanel';
import ChatToast from './ChatToast';
import { JobsList } from '../Jobs';

// ── Chat as an app a phone installs (report 8c5f3514) ────────────────────────────────────────────────────────────
// Calem: "Make an app that is downloadable to an Apple or Android phone for Chat", and, asked which kind, "(a) An
// installable web app chat only at this stage, but full function, including voice messages, file uploads, with
// direct (single click) access to jobs/company documents/quotations/invoices/receipts via the chat app".
// So this is NOT a second program: it is the chat the office already has (ChatPanel), given an address of its own
// at /chat and dressed as an app. There is one codebase, one server and one set of messages — what is written on a
// phone is in the pop-up on the desk a second later, because it is the same chat.
// What makes a phone offer to install it:
// · chat.webmanifest (public/) — the name, the icons and the address an icon opens, /chat, in its own window with
//   no browser round it. A page offers only its FIRST <link rel="manifest">, and this whole system is one
//   index.html served at every address, so that one link is pointed at the chat's own manifest while the chat is
//   the page showing and the site's is put back as it is left. On a direct load of /chat the link is not pointed at
//   all: an inline script at the top of index.html WRITES the manifest for the address being loaded, so the chat's
//   is the only one the head ever carries there. An iPhone reads the head as the page is delivered and keeps the
//   first manifest it sees, so neither doing it from here nor rewriting a delivered link was early enough, and Chat
//   installed as the portal (reports 6f73b86b, af755b3a). What is left for here is the walk INTO the chat from
//   another page, and putting the site's own back on the way out (below). Either way the portal still installs as
//   the portal from every other page, and Chat installs as Chat from here: two icons, each opening its own address.
// · sw.js (public/) — a service worker, which Android requires before it will offer "Install" at all. It stores
//   nothing, so an installed app can never be served a page older than the last deploy.
// · the apple-mobile-web-app-* meta in index.html, which is what iPhones read instead of a manifest: they make
//   "Add to Home Screen" open /chat full-screen. The icon's NAME rides with the manifest above — written by
//   index.html at /chat, set here on a walk in — so it is Chat on the chat and the system's name everywhere else.
// Signing in: /chat behind a sign-in sends the person to the sign-in page with ?return=/chat and comes straight
// back here afterwards (components/App.tsx), rather than landing them on the Dashboard with no way back to the app.
const CHAT_NAME = 'Chat';                        // what the icon is called — no firm's name, as every tenant installs it
const CHAT_MANIFEST = '/chat.webmanifest';
// The picture under that name (report edc059ac). Chat used to share the portal's icon-192 / icon-512, so the two
// apps sat on a phone as the same shield; the server now draws Chat one of its own from that same shield — smaller,
// with a blue ring round the tile (server/src/routes/chatIcon.ts). The manifest names 192 and 512; this is the one
// an iPhone reads, which has no manifest to read. ?v= is what makes a browser fetch it again after the last one.
const CHAT_ICON = '/api/chat-icon/180.png?v=4';
// The chat's viewport (report ec0c5a76: "On the iPhone make the interface automatically adapt to the screen and
// disable zooming"): no zoom of any kind, and the whole screen to its edges — viewport-fit=cover is what makes
// env(safe-area-inset-*), the room ChatPanel keeps for a home bar or a notch, more than 0. index.html writes the
// very same line on a direct load of /chat; a change to one belongs in both.
const CHAT_VIEWPORT = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';

/** The page's head while the chat app is the page: its own manifest, its own picture, its own name for an iPhone
 *  icon, and its own viewport. All four are put back as they were on the way out, so every other page installs as
 *  the portal and zooms as it always has. On a direct load of /chat index.html has written all four into the head
 *  already, before an iPhone looked (reports 6f73b86b, af755b3a, ec0c5a76); this is what sets them for a walk in
 *  from another page of the system, and what takes them off again either way. */
function useInstallable(): void {
  useEffect(() => {
    // data-site-href is the SITE's own address, which index.html writes onto both links on every page. Reading it
    // means the way out restores the SITE's manifest and picture — without it, a direct load of /chat would hand
    // every page walked to afterwards the chat's own.
    const link = document.head.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const was = link?.dataset.siteHref || link?.getAttribute('href') || '';
    link?.setAttribute('href', CHAT_MANIFEST);
    const touch = document.head.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    const wasTouch = touch?.dataset.siteHref || touch?.getAttribute('href') || '';
    touch?.setAttribute('href', CHAT_ICON);
    // iOS names a home-screen icon after this meta when the page carries one, else after the whole document title.
    // Where index.html has written one into the head already this is that same one rather than a second beside it,
    // so that taking it off below really does leave no other page of the system named "Chat".
    const already = document.head.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    const named = already || document.createElement('meta');
    named.name = 'apple-mobile-web-app-title';
    named.content = CHAT_NAME;
    if (!already) document.head.appendChild(named);
    // The one viewport meta, which index.html writes on every page with the site's own in data-site-content — so
    // the way out puts the SITE's back even after a direct load of /chat, as data-site-href does for the links.
    const view = document.head.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const wasView = view?.dataset.siteContent || view?.getAttribute('content') || '';
    view?.setAttribute('content', CHAT_VIEWPORT);
    // Nothing is cached by it, so there is no "an update is waiting" to handle: the page it answers for is always
    // the one on the server. A browser that has no service workers (or a plain http:// address) simply has none,
    // and the chat works exactly the same — only the Install offer is missing.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', { scope: '/chat' }).catch(() => { /* not installable, still usable */ });
    }
    return () => {
      if (link && was) link.setAttribute('href', was);
      if (touch && wasTouch) touch.setAttribute('href', wasTouch);
      if (view && wasView) view.setAttribute('content', wasView);
      named.remove();
    };
  }, []);
}

/** No zooming while the chat app is the page (report ec0c5a76). The viewport above already says so, and an
 *  installed app obeys it — but Safari in an ordinary browser tab ignores user-scalable=no on purpose, so a
 *  two-finger pinch is refused here as it starts (Safari's own gesture events), and touch-action: manipulation on
 *  the page's root takes away the double-tap zoom. Both come off again as the chat is left. */
function useNoZoom(): void {
  useEffect(() => {
    const refuse = (e: Event) => e.preventDefault();
    // Not passive: a passive listener's preventDefault is ignored.
    document.addEventListener('gesturestart', refuse, { passive: false });
    document.addEventListener('gesturechange', refuse, { passive: false });
    const root = document.documentElement;
    const wasAction = root.style.touchAction;
    root.style.touchAction = 'manipulation';
    return () => {
      document.removeEventListener('gesturestart', refuse);
      document.removeEventListener('gesturechange', refuse);
      root.style.touchAction = wasAction;
    };
  }, []);
}

export default function ChatApp() {
  const navigate = useNavigate();
  // Which jobs page a number pressed in a message opens over the chat, '' for none. ChatWords has already decided
  // the reader may open it at all — a job number is drawn as a link only for somebody holding Active or Closed Jobs.
  const [jobs, setJobs] = useState<'active' | 'closed' | ''>('');
  useInstallable();
  useNoZoom();

  // A job number pressed in a message (report 8c5f3514): the job opens OVER the chat, which is how it stays inside
  // the installed app — a new browser tab would hand the reader to the phone's browser and leave the app behind.
  // The address carries the job, because that is the door the Jobs page already opens one by (?open=<id>, which it
  // reads and then strips); pressing the same number again puts it back, so the job opens again.
  const openJob = (page: string, jobId: string) => {
    setJobs(page === 'closed_jobs' ? 'closed' : 'active');
    navigate(`/chat?open=${encodeURIComponent(jobId)}`);
  };

  return (
    <>
      <ChatPanel app onClose={() => { /* the app has nowhere to close to */ }} onJob={openJob} />
      {/* The notices ride along as they do on the Dashboard. They keep themselves quiet while a chat window is open
          (ChatPanel: useChatPanelOpen) and in the app one always is, so here the thread itself is the notice. */}
      <ChatToast />
      {jobs && <JobOverChat view={jobs} onClose={() => setJobs('')} />}
    </>
  );
}

/** The job a number in a message opened, over the chat, with the way back to it at the top left. It is the Active /
 *  Closed Jobs page itself, as it is on a desk — on a phone it is that page scrolled, not a page of its own shape. */
function JobOverChat({ view, onClose }: { view: 'active' | 'closed'; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    // Over the chat window (10200) and under the quotation / invoice / receipt forms (DocNoHost, 13100), so a
    // document number pressed inside the job still opens on top of it. The chat's viewport reaches the screen's
    // edges (report ec0c5a76), so this keeps clear of an iPhone's home bar, and of its notch when it is turned.
    <div style={{ position: 'fixed', inset: 0, zIndex: 13000, display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', padding: '0 env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', flexShrink: 0 }}>
        <button onClick={onClose} title={t('chat_app_back')} aria-label={t('chat_app_back')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', flexShrink: 0 }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 19 }} aria-hidden />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t(view === 'closed' ? 'mod_closed_jobs' : 'mod_active_jobs')}</div>
      </header>
      <JobsList view={view} />
    </div>
  );
}
